import React from "react";
import { NavLink } from "react-router-dom";
import { X, PanelLeftClose, PanelLeftOpen, ExternalLink } from "lucide-react";
import { useAuth } from "../dataContext/AuthContext";
import { getVisibleSections, FOOTER_LINKS } from "../layout/navConfig";

/**
 * App sidebar.
 * Desktop (md+): collapsed icon rail or pinned open (`isPinned`, toggled by the header button).
 *   Hovering the collapsed rail opens it as an overlay without shifting the page content.
 * Mobile: off-canvas drawer controlled by `isMobileOpen`.
 */
function Sidebar({ isPinned, onTogglePinned, isHovered, onHoverChange, isMobileOpen, onMobileClose }) {
  const { roles } = useAuth();
  const isExpanded = isPinned || isHovered || isMobileOpen;
  const sections = getVisibleSections(roles);
  const footerLinks = FOOTER_LINKS.filter((link) => !link.visible || link.visible(roles));
  const closeOnMobile = () => isMobileOpen && onMobileClose?.();

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={onMobileClose} aria-hidden="true" />
      )}
      <aside
        className={`bg-white h-screen fixed left-0 top-0 flex flex-col border-r border-gray-200 transition-all duration-200 ease-in-out z-50 w-72 max-w-[85vw]
          ${isExpanded ? "md:w-64" : "md:w-16"}
          ${!isPinned && isHovered ? "md:shadow-xl" : ""}
          ${isMobileOpen ? "translate-x-0 shadow-xl" : "-translate-x-full"} md:translate-x-0`}
        onMouseEnter={() => !isPinned && onHoverChange?.(true)}
        onMouseLeave={() => onHoverChange?.(false)}
        aria-label="Main navigation"
      >
        {/* Header */}
        <div className={`h-16 flex items-center gap-2 px-3 border-b border-gray-200 ${isExpanded ? "" : "md:justify-center"}`}>
          <button
            type="button"
            onClick={onTogglePinned}
            className="hidden md:inline-flex p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label={isPinned ? "Collapse sidebar" : "Keep sidebar open"}
            title={isPinned ? "Collapse sidebar" : "Keep sidebar open"}
          >
            {isPinned ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeftOpen className="h-5 w-5" />}
          </button>
          {isExpanded && (
            <span className="text-lg font-bold text-gray-900 flex-1 truncate">
              {roles.includes("super_admin") ? "Senzo Admin" : "Senzo"}
            </span>
          )}
          {isMobileOpen && (
            <button
              type="button"
              onClick={onMobileClose}
              className="ml-auto p-2 rounded-lg text-gray-500 hover:bg-gray-100 md:hidden"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Sections */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3" onClick={closeOnMobile}>
          {sections.map((section, index) => (
            <div key={section.title} className={index > 0 ? "mt-4" : ""}>
              {isExpanded ? (
                <p className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{section.title}</p>
              ) : (
                index > 0 && <div className="mx-3 mb-3 border-t border-gray-200" />
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <SidebarItem item={item} isExpanded={isExpanded} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <ul className="p-2 border-t border-gray-200 space-y-0.5" onClick={closeOnMobile}>
          {footerLinks.map((link) => (
            <li key={link.text}>
              {link.to ? (
                <SidebarItem item={link} isExpanded={isExpanded} />
              ) : (
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={isExpanded ? undefined : link.text}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 ${isExpanded ? "" : "md:justify-center md:px-0"}`}
                >
                  <link.icon className="h-5 w-5 flex-shrink-0" />
                  {isExpanded && (
                    <>
                      <span className="flex-1 truncate">{link.text}</span>
                      <ExternalLink className="h-3.5 w-3.5 text-gray-400" aria-label="(opens in new tab)" />
                    </>
                  )}
                </a>
              )}
            </li>
          ))}
        </ul>
      </aside>
    </>
  );
}

function SidebarItem({ item, isExpanded }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={isExpanded ? undefined : item.text}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isExpanded ? "" : "md:justify-center md:px-0"
        } ${isActive ? "bg-blue-600 text-white shadow-sm" : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"}`
      }
    >
      <Icon className="h-5 w-5 flex-shrink-0" />
      {isExpanded && <span className="truncate">{item.text}</span>}
    </NavLink>
  );
}

export default Sidebar;
